import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = "/api";

const getSessionId = () => {
  let id = localStorage.getItem("csm_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("csm_chat_session", id);
  }
  return id;
};

export default function ChatWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hey — I'm the Cyber Smoke concierge (AI). Ask me about services, exact pricing, or delivery times." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  if (location.pathname.startsWith("/admin")) return null;

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection hiccup — try again, or use the contact form." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-button"
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 left-6 z-50 flex h-13 w-13 items-center justify-center border border-[#E11D2E]/50 bg-[#0D0D0D] p-3.5 text-[#E11D2E] shadow-[0_0_32px_rgba(225,29,46,0.25)]"
        aria-label="Open chat"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 left-6 z-50 flex h-[460px] w-[340px] flex-col border border-[#1F1F1F] bg-[#050505] shadow-[0_0_60px_rgba(225,29,46,0.15)] sm:w-[380px]"
          >
            <div className="flex items-center gap-2.5 border-b border-[#1F1F1F] bg-[#0D0D0D] px-4 py-3.5">
              <Sparkles size={15} className="text-[#E11D2E]" />
              <div>
                <p className="text-sm font-600 text-white">Concierge</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A]">AI assistant</p>
              </div>
            </div>
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} data-testid={`chat-message-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#E11D2E] text-white" : "border border-[#1F1F1F] bg-[#0D0D0D] text-[#d4d4d4]"}`}>
                    {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={send} className="flex border-t border-[#1F1F1F]">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about pricing, delivery…"
                className="flex-1 bg-transparent px-4 py-3.5 text-sm text-white placeholder-[#555555] outline-none"
              />
              <button data-testid="chat-send-button" type="submit" disabled={streaming} className="px-4 text-[#E11D2E] transition-colors hover:text-[#F13B47] disabled:opacity-40" aria-label="Send">
                <Send size={17} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
